import React, { useState } from 'react';

// Color scheme for different rule types
const getRuleColor = (ruleId, ruleName) => {
  const ruleIdLower = (ruleId || '').toLowerCase();
  const ruleNameLower = (ruleName || '').toLowerCase();

  // Svara (accent) rules - blue tones
  if (ruleIdLower.includes('svara') || ruleIdLower.includes('svrita') ||
      ruleIdLower.includes('udatta') || ruleIdLower.includes('anudatta') ||
      ruleIdLower.includes('svarita') || ruleNameLower.includes('स्वर') ||
      ruleNameLower.includes('उदात्त') || ruleNameLower.includes('अनुदात्त')) {
    return { background: '#e3f2fd', border: '#1976d2', text: '#1565c0' };
  }

  // Sandhi rules - orange/amber tones
  if (ruleIdLower.includes('sandhi') || ruleIdLower.includes('visarga') ||
      ruleNameLower.includes('सन्धि') || ruleNameLower.includes('विसर्ग')) {
    return { background: '#fff3e0', border: '#ff9800', text: '#e65100' };
  }

  // Default - green tones
  return { background: '#e8f5e9', border: '#4caf50', text: '#2e7d32' };
};

const RuleTooltip = ({ step, position, onClose }) => {
  const colors = getRuleColor(step.ruleId, step.ruleName);

  return (
    <div
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y + 30,
        backgroundColor: 'white',
        border: `2px solid ${colors.border}`,
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 1000,
        minWidth: '280px',
        maxWidth: '400px',
        fontFamily: 'VijayaDV, sans-serif'
      }}
      onMouseLeave={onClose}
    >
      <div style={{
        fontWeight: 'bold',
        fontSize: '16px',
        color: colors.text,
        marginBottom: '8px',
        borderBottom: `1px solid ${colors.border}`,
        paddingBottom: '6px'
      }}>
        {step.ruleName}
        {step.ruleNameEnglish && (
          <span style={{ fontWeight: 'normal', color: '#666', fontSize: '14px', marginLeft: '8px' }}>
            ({step.ruleNameEnglish})
          </span>
        )}
      </div>

      {step.sutra && (
        <div style={{
          backgroundColor: colors.background,
          padding: '8px',
          borderRadius: '4px',
          marginBottom: '8px',
          fontSize: '14px'
        }}>
          <span style={{ fontWeight: 'bold' }}>सूत्रम्: </span>
          {step.sutra}
        </div>
      )}

      <div style={{
        fontSize: '18px',
        textAlign: 'center',
        padding: '8px',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px'
      }}>
        {step.pattern}
      </div>

      {step.explanation && (
        <div style={{
          marginTop: '8px',
          fontSize: '13px',
          color: '#555',
          fontStyle: 'italic'
        }}>
          {step.explanation}
        </div>
      )}
    </div>
  );
};

const RuleTraceDisplay = ({ ruleTrace, outputText }) => {
  const [hoveredStep, setHoveredStep] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  if (!ruleTrace || !ruleTrace.steps || ruleTrace.steps.length === 0) {
    return null;
  }

  const handleStepHover = (step, event) => {
    const rect = event.target.getBoundingClientRect();
    setTooltipPosition({ x: 0, y: rect.height });
    setHoveredStep(step);
  };

  return (
    <div style={{
      marginTop: '15px',
      fontFamily: 'VijayaDV, sans-serif'
    }}>
      {/* Rule chips - inline with output */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        alignItems: 'center'
      }}>
        <span style={{
          fontWeight: 'bold',
          fontSize: '14px',
          color: '#666',
          marginRight: '4px'
        }}>
          नियमाः:
        </span>
        {ruleTrace.steps.map((step, index) => {
          const colors = getRuleColor(step.ruleId, step.ruleName);
          return (
            <div
              key={index}
              style={{ position: 'relative', display: 'inline-block' }}
              onMouseEnter={(e) => handleStepHover(step, e)}
              onMouseLeave={() => setHoveredStep(null)}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 10px',
                  backgroundColor: colors.background,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '16px',
                  fontSize: '14px',
                  color: colors.text,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: hoveredStep === step ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                  transform: hoveredStep === step ? 'scale(1.02)' : 'scale(1)'
                }}
              >
                <span style={{
                  backgroundColor: colors.border,
                  color: 'white',
                  borderRadius: '50%',
                  width: '18px',
                  height: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: 'bold'
                }}>
                  {index + 1}
                </span>
                {step.ruleName}
              </span>
              {hoveredStep === step && (
                <RuleTooltip
                  step={step}
                  position={tooltipPosition}
                  onClose={() => setHoveredStep(null)}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        gap: '15px',
        marginTop: '10px',
        fontSize: '12px',
        color: '#666'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{
            width: '12px',
            height: '12px',
            backgroundColor: '#fff3e0',
            border: '1px solid #ff9800',
            borderRadius: '3px'
          }}></span>
          सन्धि-नियमाः (Sandhi)
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{
            width: '12px',
            height: '12px',
            backgroundColor: '#e3f2fd',
            border: '1px solid #1976d2',
            borderRadius: '3px'
          }}></span>
          स्वर-नियमाः (Svara)
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{
            width: '12px',
            height: '12px',
            backgroundColor: '#e8f5e9',
            border: '1px solid #4caf50',
            borderRadius: '3px'
          }}></span>
          अन्ये (Other)
        </div>
      </div>
    </div>
  );
};

export default RuleTraceDisplay;
